import {
  createContext,
  useEffect,
  useContext,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react"

import { getValidationSchema } from "./types"
import { fieldIsVisible, fieldDoesNotBlur } from "./helpers"
import transform from "./transform"

import DefaultLayout from "./DefaultLayout"
import { Button } from "web/components"

import {
  filterKeys,
  isAValue,
  plainObjectFromValue,
} from "web/services/helpers"

const nullFn = () => null

const FormContext = createContext()
FormContext.displayName = "FormContext"
export default function Form({
  value: valueFromProps,
  globalValue,
  fields: fieldsFromProps,
  children,
  render,
  onChange,
  onFieldChanged,
  onSubmit,
  onCancel,
  submitText,
  placeholder = "",
}) {
  const isMounted = useRef(false)

  /**
   * Using state for form value to allow for invalid values locally.
   * onChange is only called with valid values.
   */
  const [formValue, setFormValue] = useState(valueFromProps || {})

  // need ref because this value is used inside debounced callbacks
  const formValueRef = useRef(formValue)
  function instantSetFormValue(value) {
    formValueRef.current = value
    setFormValue(value)
  }

  const [formErrors, setFormErrors] = useState({})

  // need ref because this value is used inside debounced callbacks
  const formErrorsRef = useRef(formErrors)
  function instantSetFormErrors(errors) {
    formErrorsRef.current = errors
    setFormErrors(errors)
  }

  const formIsValid = Object.entries(formErrors).length === 0

  const [formTouchedFields, setFormTouchedFields] = useState(
    fieldsFromProps.reduce((obj, field) => ({ ...obj, [field.key]: false }), {})
  )

  function touchAllFields() {
    setFormTouchedFields(
      fieldsFromProps.reduce(
        (obj, field) => ({ ...obj, [field.key]: true }),
        {}
      )
    )
  }

  const valueForConditions = useMemo(() => {
    return plainObjectFromValue(globalValue || formValue)
  }, [globalValue, formValue])

  function fieldVisibility(field) {
    if (field.isVisible === undefined) {
      return fieldIsVisible(field, valueForConditions)
    }

    return field.isVisible
  }

  const fields = useMemo(() => {
    return fieldsFromProps.map((field) => {
      const isVisible = fieldVisibility(field)
      const isRequired = isVisible && field.required

      return {
        ...field,
        isRequired,
        isVisible,
        isReadOnly: isAValue(field.expression),
      }
    })
  }, [fieldsFromProps, valueForConditions])

  const fieldsRef = useRef()
  fieldsRef.current = fields

  const validationSchema = useMemo(() => getValidationSchema(fields), [fields])

  useEffect(() => {
    if (isMounted.current) {
      const newValue = valueFromProps || {}
      instantSetFormValue(newValue)
      validateForm(newValue)
    } else {
      validateForm(formValueRef.current)
      isMounted.current = true
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueFromProps])

  function pruneValue(value) {
    // only return values in currently shown fields
    const fields = fieldsRef.current

    const visibleFieldKeys = fields.filter((f) => f.isVisible).map((f) => f.key)
    const prunedValue = filterKeys(value, visibleFieldKeys)

    // strip null values from result
    for (const key of Object.keys(prunedValue)) {
      const testValue = prunedValue[key]
      if (testValue === null || testValue === "-" || testValue === "") {
        delete prunedValue[key]
      }
    }

    return prunedValue
  }

  function transformValue(value) {
    const transformedValue = { ...value }

    for (const field of fields.filter((field) => isAValue(value[field.key]))) {
      const valueArray = field.multiple_values
        ? value[field.key]
        : [value[field.key]]

      const transformedValueArray = valueArray.map((el) => ({
        ...el,
        value: transform(field, el.value),
      }))

      transformedValue[field.key] = field.multiple_values
        ? transformedValueArray
        : transformedValueArray[0]
    }

    return transformedValue
  }

  const validateForm = useCallback(
    async (value = formValue) => {
      const plainObject = plainObjectFromValue(value)

      try {
        await validationSchema.validate(plainObject, { abortEarly: false })

        // prevent unnecessary re-render, which interrupts button clicks
        if (Object.entries(formErrorsRef.current).length !== 0) {
          instantSetFormErrors({})
        }
      } catch (error) {
        instantSetFormErrors(
          error.inner.reduce(
            (result, { path, message }) => ({ ...result, [path]: message }),
            {}
          )
        )
      }
    },
    [formValue, validationSchema]
  )

  useEffect(() => {
    validateForm()
  }, [fields, validateForm])

  function updateFieldAndFormValue(fieldKey, formValue) {
    const transformedValue = transformValue(formValue)

    const fieldError = formErrorsRef.current[fieldKey]
    const fieldHasInvalidValue =
      fieldError !== undefined && fieldError !== "Required."

    if (onFieldChanged && !fieldHasInvalidValue) {
      onFieldChanged(fieldKey, transformedValue[fieldKey])
    }

    const formHasNoErrors = Object.entries(formErrorsRef.current).length === 0

    if (onChange && formHasNoErrors) {
      onChange(transformedValue)
    }
  }

  async function handleFieldChange(fieldKey, value) {
    // Need to use ref to ensure changes are additive if this method
    // is called multiple times before async formValue state is updated.
    const newValue = { ...formValueRef.current, [fieldKey]: value }

    instantSetFormValue(newValue)
    await validateForm(newValue)

    // if form valid, call onChange for fields that don't blur
    const field = fields.find((f) => f.key === fieldKey)
    if (fieldDoesNotBlur(field)) {
      updateFieldAndFormValue(fieldKey, newValue)
    }
  }

  async function handleFieldFocus(fieldKey) {
    // If it doesn't blur set touch on focus
    const field = fields.find((f) => f.key === fieldKey)
    if (fieldDoesNotBlur(field)) {
      touchField(fieldKey)
    }
  }

  const setTouchedFieldsTimeoutHandle = useRef()

  const touchField = (fieldKey) => {
    if (!formTouchedFields[fieldKey]) {
      setTouchedFieldsTimeoutHandle.current = setTimeout(
        () =>
          setFormTouchedFields((prevTouched) => ({
            ...prevTouched,
            [fieldKey]: true,
          })),
        250
      )
    }
  }

  // clear timeout when form unmounts
  useEffect(() => {
    return () => {
      if (setTouchedFieldsTimeoutHandle.current) {
        clearTimeout(setTouchedFieldsTimeoutHandle.current)
      }
    }
  }, [])

  async function handleFieldBlur(fieldKey) {
    touchField(fieldKey)
    await validateForm(formValueRef.current)
    updateFieldAndFormValue(fieldKey, formValueRef.current)
  }

  async function handleSubmit() {
    if (!formIsValid) {
      touchAllFields()
      return
    }

    if (onSubmit) {
      onSubmit(transformValue(pruneValue(formValue)))
    }
  }

  function handleCancel() {
    onCancel()
  }

  const Submit = onSubmit
    ? () => (
        <Button
          testId="button-form-submit"
          size="medium"
          onClick={handleSubmit}
          disabled={!formIsValid}
        >
          {submitText || "Submit"}
        </Button>
      )
    : nullFn

  const Cancel = onCancel
    ? () => (
        <Button size="medium" onClick={handleCancel} variant="tertiary_default">
          Cancel
        </Button>
      )
    : nullFn

  const controls = { Submit, Cancel }

  const context = {
    value: formValue,
    valueForConditions: valueForConditions,
    errors: formErrors,
    isValid: formIsValid,
    touched: formTouchedFields,
    fields: fields,
    onFieldChange: handleFieldChange,
    onFieldFocus: handleFieldFocus,
    onFieldBlur: handleFieldBlur,
    placeholder: placeholder,
    controls: controls,
    submit: handleSubmit,
    cancel: handleCancel,
  }

  return (
    <FormContext.Provider value={context}>
      {render === undefined && (
        <DefaultLayout
          fields={fields.filter((f) => f.isVisible)}
          controls={controls}
        >
          {children}
        </DefaultLayout>
      )}
      {render !== undefined && render(context)}
    </FormContext.Provider>
  )
}

export function useFormContext() {
  return useContext(FormContext)
}
